import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roleLabel as roleDisplayName } from '../lib/roleLabel';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { CommonUsagePanel } from '../components/CommonUsagePanel';
import type { DataTableColumn } from '@ui-kit';
import { useUserList } from '../hooks/useUserList';
import { useCreateUser } from '../hooks/useCreateUser';
import { useLockUser } from '../hooks/useLockUser';
import { useUnlockUser } from '../hooks/useUnlockUser';
import { UserCreateDialog } from '../components/UserCreateDialog';
import { UserLockToggle } from '../components/UserLockToggle';
import { userApi } from '../api/userApi';
import { useMe } from '../../_shared/hooks/useMe';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import type { UserListItem, UserSearchCondition, ApiError } from '../types/user';
import type { UserCreateFormValues } from '../schema/userSchema';

const DEFAULT_PAGE_SIZE = 20;

function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return '-';
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return `${days}일 전`;
}

// 5행 Skeleton row 렌더러
function SkeletonRow() {
  return (
    <tr>
      {[40, 80, 60, 50, 60, 60].map((w, i) => (
        <td
          key={i}
          style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}
        >
          <div
            style={{
              height: 14,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

export function UserListPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const meQuery = useMe();
  const currentUserId = meQuery.data?.id;
  const hasUserWrite = useHasPermission('USER_WRITE');

  // 동적 역할 목록
  const rolesQuery = useAssignableRoles();
  const assignableRoles = rolesQuery.data ?? [];

  // 검색/필터 state
  const [keyword, setKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');
  const [lockedFilter, setLockedFilter] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('createdAt,desc');

  // 적용된 필터 (검색 버튼 클릭 시 반영)
  const [appliedFilters, setAppliedFilters] = useState<UserSearchCondition>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: 'createdAt,desc',
  });

  // 다이얼로그 state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [lockDialogTarget, setLockDialogTarget] = useState<UserListItem | null>(null);
  const [createError, setCreateError] = useState<string | undefined>();
  const [lockError, setLockError] = useState<string | undefined>();
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // 성공 토스트 state (4500ms 자동 소멸)
  const [successToast, setSuccessToast] = useState<string | null>(null);

  useEffect(() => {
    if (!successToast) return;
    const timer = setTimeout(() => setSuccessToast(null), 4500);
    return () => clearTimeout(timer);
  }, [successToast]);

  const listQuery = useUserList(appliedFilters);
  const createMutation = useCreateUser();

  const lockMutation = useLockUser(lockDialogTarget?.id ?? 0);
  const unlockMutation = useUnlockUser(lockDialogTarget?.id ?? 0);

  const pageData = listQuery.data;

  const handleSearch = useCallback(() => {
    const filters: UserSearchCondition = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort,
    };
    if (keyword.trim()) filters.keyword = keyword.trim();
    if (roleFilter) filters.role = roleFilter;
    if (enabledFilter !== '') filters.enabled = enabledFilter === 'true';
    if (lockedFilter !== '') filters.locked = lockedFilter === 'true';
    setPage(0);
    setAppliedFilters(filters);
  }, [keyword, roleFilter, enabledFilter, lockedFilter, sort]);

  const handleReset = useCallback(() => {
    setKeyword('');
    setRoleFilter('');
    setEnabledFilter('');
    setLockedFilter('');
    setSort('createdAt,desc');
    setPage(0);
    setAppliedFilters({ page: 0, size: DEFAULT_PAGE_SIZE, sort: 'createdAt,desc' });
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      setAppliedFilters((prev) => ({ ...prev, page: newPage }));
    },
    []
  );

  /** 백엔드 에러 코드를 i18n 키로 매핑 (R7-054) */
  const resolveErrorMessage = useCallback(
    (err: ApiError): string => {
      if (err.code && t(`user.error.${err.code}`) !== `user.error.${err.code}`) {
        return t(`user.error.${err.code}`);
      }
      return err.message ?? t('user.error.network');
    },
    [t]
  );

  const handleCreateSubmit = useCallback(
    (values: UserCreateFormValues) => {
      setCreateError(undefined);
      createMutation.mutate(
        {
          email: values.email,
          displayName: values.displayName,
          role: values.role,
          generateTemporaryPassword: values.generateTemporaryPassword,
          initialPassword: values.initialPassword,
        },
        {
          onSuccess: (data) => {
            setCreateDialogOpen(false);
            if (data.temporaryPassword) {
              setTempPassword(data.temporaryPassword);
            } else {
              setSuccessToast(t('user.toast.created.noPassword'));
            }
          },
          onError: (err: unknown) => {
            setCreateError(resolveErrorMessage(err as ApiError));
          },
        }
      );
    },
    [createMutation, resolveErrorMessage, t]
  );

  const handleLockToggle = useCallback(() => {
    if (!lockDialogTarget) return;
    setLockError(undefined);
    const onError = (err: unknown) => {
      const apiErr = err as ApiError;
      setLockError(apiErr.message ?? '작업에 실패했습니다');
    };
    const onSuccess = () => {
      const wasLocked = lockDialogTarget.locked;
      setLockDialogTarget(null);
      setLockError(undefined);
      setSuccessToast(wasLocked ? t('user.toast.unlocked') : t('user.toast.locked'));
    };

    if (lockDialogTarget.locked) {
      unlockMutation.mutate(undefined, { onSuccess, onError });
    } else {
      lockMutation.mutate(undefined, { onSuccess, onError });
    }
  }, [lockDialogTarget, lockMutation, unlockMutation]);

  const handleExport = useCallback(async () => {
    try {
      const blob = await userApi.export({
        keyword: appliedFilters.keyword,
        role: appliedFilters.role,
        enabled: appliedFilters.enabled,
        locked: appliedFilters.locked,
        sort: appliedFilters.sort,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // 에러 처리 생략 (toast 미구현)
    }
  }, [appliedFilters]);

  const columns: DataTableColumn<UserListItem>[] = [
    {
      key: 'email',
      header: t('user.list.column.email'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {row.email}
        </span>
      ),
    },
    {
      key: 'displayName',
      header: t('user.list.column.displayName'),
    },
    {
      key: 'role',
      header: t('user.list.column.role'),
      render: (row) => {
        const roleKey = row.role.replace('ROLE_', '').toLowerCase();
        const roleLabel = t(`appshell.user.role.${roleKey}`, { defaultValue: row.role });
        return (
          <span
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              padding: '1px var(--spacing-2)',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${
                row.role === 'ROLE_ADMIN'
                  ? 'var(--color-brand-primary)'
                  : row.role === 'ROLE_MANAGER'
                  ? 'var(--color-info)'
                  : 'var(--color-surface-border)'
              }`,
              color:
                row.role === 'ROLE_ADMIN'
                  ? 'var(--color-brand-primary)'
                  : row.role === 'ROLE_MANAGER'
                  ? 'var(--color-info)'
                  : 'var(--color-text-secondary)',
              background: 'var(--color-surface-elevated)',
            }}
          >
            {roleLabel}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: t('user.list.column.status'),
      render: (row) => {
        if (row.locked) {
          return <StatusPill tone="danger">{t('user.status.locked')}</StatusPill>;
        }
        if (!row.enabled) {
          return <StatusPill tone="neutral">{t('user.status.inactive')}</StatusPill>;
        }
        return <StatusPill tone="success">{t('user.status.active')}</StatusPill>;
      },
    },
    {
      key: 'lastLoginAt',
      header: t('user.list.column.lastLogin'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {formatRelativeTime(row.lastLoginAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('user.list.column.actions'),
      width: '100px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 'var(--spacing-1)', justifyContent: 'flex-end' }}>
          <button
            type="button"
            aria-label={`${row.displayName} ${t('user.list.action.detail')}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/users/${row.id}`);
            }}
            style={{
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px var(--spacing-2)',
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            {t('user.list.action.detail')}
          </button>
          <button
            type="button"
            aria-label={row.locked ? `${row.displayName} ${t('user.detail.action.unlock')}` : `${row.displayName} ${t('user.detail.action.lock')}`}
            disabled={row.id === currentUserId}
            title={row.id === currentUserId ? t('user.action.self.disabled.tooltip') : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setLockError(undefined);
              setLockDialogTarget(row);
            }}
            style={{
              background: row.locked ? 'color-mix(in srgb, var(--color-success) 15%, transparent)' : 'color-mix(in srgb, var(--color-warning) 15%, transparent)',
              border: `1px solid ${row.locked ? 'var(--color-success)' : 'var(--color-warning)'}`,
              borderRadius: 'var(--radius-sm)',
              padding: '2px var(--spacing-2)',
              fontSize: 'var(--font-size-xs)',
              cursor: row.id === currentUserId ? 'not-allowed' : 'pointer',
              color: row.locked ? 'var(--color-success)' : 'var(--color-warning)',
              opacity: row.id === currentUserId ? 0.5 : 1,
            }}
          >
            {row.locked ? t('user.list.action.unlock') : t('user.list.action.lock')}
          </button>
        </div>
      ),
    },
  ];

  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && pageData?.empty;
  const showTable = !isListLoading && !isListError && !isEmpty;

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSecondary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
  };

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* 성공 토스트 (4500ms 자동 소멸) */}
      {successToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{successToast}</span>
          <button
            type="button"
            onClick={() => setSuccessToast(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-lg)',
              lineHeight: 1,
            }}
            aria-label={t('user.toast.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      {/* 임시 비밀번호 토스트 */}
      {tempPassword && (
        <div
          role="alert"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            {t('user.toast.created.withPassword')}{' '}
            <code
              style={{
                fontFamily: 'var(--font-family-mono)',
                padding: '1px var(--spacing-2)',
                background: 'var(--color-surface-elevated)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {tempPassword}
            </code>
          </span>
          <button
            type="button"
            onClick={() => setTempPassword(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-lg)',
            }}
            aria-label={t('user.toast.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      {/* 페이지 헤더 */}
      <PageHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {t('user.list.title')}
            {/* 읽기 전용 배지 — USER_WRITE 권한 없는 경우 */}
            {!hasUserWrite && (
              <StatusPill tone="neutral">
                {t('user.badge.readonly')}
              </StatusPill>
            )}
          </span>
        }
        breadcrumb={<span>{t('user.list.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              onClick={() => void handleExport()}
              style={btnSecondary}
              aria-label={t('user.list.button.export')}
            >
              {t('user.list.button.export')}
            </button>
            {/* ButtonGuard: MANAGER lacks USER_WRITE → disabled per #324 */}
            <ButtonGuard requirePermission="USER_WRITE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    setCreateError(undefined);
                    setCreateDialogOpen(true);
                  }}
                  disabled={disabled}
                  title={reason}
                  style={{
                    ...btnPrimary,
                    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  aria-label={t('user.list.button.create')}
                  aria-disabled={disabled}
                >
                  + {t('user.list.button.create')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* 통계 요약 */}
      {pageData && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 'var(--spacing-2) 0 var(--spacing-4)' }}>
          {t('user.list.stats.total', { total: pageData.totalElements })}
        </p>
      )}

      {/* 검색/필터 바 (R7-052: form[role=search]) */}
      <form
        role="search"
        aria-label={t('user.list.search.aria')}
        onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 180px 140px 140px auto',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'end',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label htmlFor="user-search-keyword" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            {t('user.list.filter.search')}
          </label>
          <input
            id="user-search-keyword"
            type="text"
            placeholder={t('user.list.search.placeholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label htmlFor="user-filter-role" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            {t('user.list.filter.role')}
          </label>
          <select
            id="user-filter-role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label={t('user.list.filter.role')}
            style={inputStyle}
          >
            <option value="">{t('user.list.filter.all')}</option>
            {assignableRoles.map((r) => (
              <option key={r.code} value={r.code}>
                {roleDisplayName(r, i18n.language)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label htmlFor="user-filter-enabled" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            {t('user.list.filter.enabled')}
          </label>
          <select
            id="user-filter-enabled"
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value as '' | 'true' | 'false')}
            style={inputStyle}
          >
            <option value="">{t('user.list.filter.all')}</option>
            <option value="true">{t('user.status.active')}</option>
            <option value="false">{t('user.status.inactive')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label htmlFor="user-filter-locked" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            {t('user.list.filter.locked')}
          </label>
          <select
            id="user-filter-locked"
            value={lockedFilter}
            onChange={(e) => setLockedFilter(e.target.value as '' | 'true' | 'false')}
            style={inputStyle}
          >
            <option value="">{t('user.list.filter.all')}</option>
            <option value="true">{t('user.status.locked')}</option>
            <option value="false">{t('user.list.filter.normal')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button type="submit" style={btnPrimary}>
            {t('user.list.filter.search')}
          </button>
          <button type="button" onClick={handleReset} style={btnSecondary}>
            {t('user.list.filter.reset')}
          </button>
        </div>
      </form>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading — skeleton */}
        {isListLoading && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[t('user.list.column.email'), t('user.list.column.displayName'), t('user.list.column.role'), t('user.list.column.status'), t('user.list.column.lastLogin'), ''].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('user.list.error.title')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('user.error.AUTH-403-001')
                  : ((listQuery.error as unknown as ApiError)?.message ?? t('user.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('user.list.empty.title')}
              description={t('user.list.empty.description')}
              action={
                <ButtonGuard requirePermission="USER_WRITE">
                  {({ disabled, reason }) => (
                    <button
                      type="button"
                      onClick={() => {
                        if (disabled) return;
                        setCreateError(undefined);
                        setCreateDialogOpen(true);
                      }}
                      disabled={disabled}
                      title={reason}
                      style={{
                        ...btnPrimary,
                        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                      }}
                    >
                      + {t('user.list.button.create')}
                    </button>
                  )}
                </ButtonGuard>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            <div
              style={{ overflowX: 'auto' }}
              role="region"
              aria-label={t('user.list.region.aria')}
            >
              <DataTable
                columns={columns}
                rows={pageData.content}
                rowKey={(row) => row.id}
              />
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('user.pagination.info', {
                  total: pageData.totalElements,
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('user.pagination.aria')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('user.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('user.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('user.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Dialogs */}
      <UserCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        errorMessage={createError}
      />

      {lockDialogTarget && (
        <UserLockToggle
          open={lockDialogTarget !== null}
          email={lockDialogTarget.email}
          isLocked={lockDialogTarget.locked}
          isSelf={lockDialogTarget.id === currentUserId}
          onClose={() => {
            setLockDialogTarget(null);
            setLockError(undefined);
          }}
          onConfirm={handleLockToggle}
          isLoading={lockMutation.isPending || unlockMutation.isPending}
          errorMessage={lockError}
        />
      )}

      {/* §0.4 사용 공통 모듈 패널 (R7-059) */}
      <CommonUsagePanel />
    </div>
  );
}
